/**
 * lib/supabase.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Supabase client singleton.
 *
 * When VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not configured,
 * `supabase` is null and `isSupabaseEnabled` is false.
 * All auth and sync operations degrade to silent no-ops, preserving
 * full backward compatibility for anonymous users.
 *
 * Environment variables required (add to .env.local):
 *   VITE_SUPABASE_URL       = https://<project-ref>.supabase.co
 *   VITE_SUPABASE_ANON_KEY  = eyJ... (anon/public key from Supabase dashboard)
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL      as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const supabase: SupabaseClient | null =
  SUPABASE_URL && SUPABASE_ANON_KEY
    ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: {
          persistSession:   true,
          autoRefreshToken: true,
          // Store session in localStorage so it survives page refreshes
          storage:          typeof window !== "undefined" ? window.localStorage : undefined,
        },
      })
    : null;

/** True when Supabase is configured and auth/sync features are available. */
export const isSupabaseEnabled = !!supabase;

// ─── Database type aliases ────────────────────────────────────────────────────
// Extend here as the schema grows.

export type DbTravelerProfile = {
  user_id:               string;
  traveler_type:         string | null;
  trip_purpose:          string | null;
  budget_style:          string | null;
  pace:                  string | null;
  food_style:            string | null;
  social_energy:         string | null;
  interest_counts:       Record<string, number>;
  pace_counts:           Record<string, number>;
  traveler_type_counts:  Record<string, number>;
  budget_style_counts:   Record<string, number>;
  cities_explored:       string[];
  generation_count:      number;
  // Future hooks (reserved)
  favorite_destinations: string[];
  saved_journeys:        unknown[];
  provider_clicks:       Record<string, number>;
  created_at:            string;
  updated_at:            string;
};

export type DbJourneyHistory = {
  id:             string;
  user_id:        string;
  journey_title:  string;
  journey_tone:   string | null;
  cities:         string[];
  countries:      string[];
  total_days:     number;
  interests:      string[];
  created_at:     string;
};
